import moment from "moment";
import React, { useState, useContext, useEffect } from "react";
import {
  Breadcrumb,
  Button,
  Form,
  OverlayTrigger,
  Tab,
  Tabs,
  Tooltip
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiSearch, BiFilter } from "react-icons/bi";
import { BsDownload } from "react-icons/bs";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";

import { LoaderContext } from "../../../../App";
import FormService from "../../../../core/service/FormService";
import { STATUS, LS_DCE_SUBMISSION_FILTER_KEY } from "../../../../core/constants/common";
import "../Forms.scss";
import ApprovalsTab from "./ApprovalsTab";
import ResponsesTab from "./ResponsesTab";
import SummaryTab from "./SummaryTab";
import AssessmentFilterPanel from "./AssessmentFilterPanel";

function AssessmentSubmissionList({ t }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { self } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRoute = window.location.pathname;
  const assessmentName = location.state?.assessmentName ?? '';
  const qualitativeDataStatus = location.state?.qual ?? '';
  const { setLoaderDisplay } = useContext(LoaderContext);

  const getTabFromQueryParam = (tabParam) => {
    if (!tabParam || tabParam.trim() === '') return 'summary';
    switch (tabParam.toLowerCase()) {
      case 'res':
        return 'responses';
      case 'sum':
        return 'summary';
      case 'app':
        return 'approvals';
      default:
        return 'summary';
    }
  };

  const getQueryParamFromTab = (tabKey) => {
    switch (tabKey) {
      case 'responses':
        return 'res';
      case 'summary':
        return 'sum';
      case 'approvals':
        return 'app';
      default:
        return 'sum';
    }
  };

  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      return getTabFromQueryParam(tabParam);
    }
    return location.state?.activeTab ?? 'summary';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [searchKey, setSearchKey] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [assessmentNameState, setAssessmentNameState] = useState(location.state?.assessmentName ?? '');

  const getAssessmentSubmissionFilterFromStorage = (tabKey) => {
    const savedData = localStorage.getItem(LS_DCE_SUBMISSION_FILTER_KEY);
    if (!savedData) return null;

    const parsed = JSON.parse(savedData);
    return parsed[tabKey] || null;
  };

  const saveAssessmentSubmissionFilterToStorage = (tabKey, filterData) => {
    const savedData = localStorage.getItem(LS_DCE_SUBMISSION_FILTER_KEY);
    const allFilters = savedData ? JSON.parse(savedData) : {};
    allFilters[tabKey] = filterData;
    localStorage.setItem(LS_DCE_SUBMISSION_FILTER_KEY, JSON.stringify(allFilters));
  };

  // Get initial filters for a specific tab
  const getInitialFiltersForTab = (tabKey) => {
    const savedFilters = getAssessmentSubmissionFilterFromStorage(tabKey);
    if (savedFilters) {
      return savedFilters;
    }
    return {
      parentStatusFilters: {},
      childStatusFilters: {},
      parentStatus: null,
      childStatus: null,
      submittedDate: null
    };
  };

  const [filters, setFilters] = useState(() => getInitialFiltersForTab(getInitialTab()));

  const getAssessmentTitle = () => {
    const name = assessmentNameState || assessmentName;
    return name || t("AssessmentSubmissionList.title.label");
  };

  const handleSearch = (val) => {
    setSearchKey(val);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(inputValue);
    }
  };

  // Load filters from localStorage on mount and sync tab from query param
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const initialTab = tabParam ? getTabFromQueryParam(tabParam) : (location.state?.activeTab ?? 'summary');

    // Set active tab if it's different from current
    if (initialTab !== activeTab) {
      setActiveTab(initialTab);
    }

    // Persist assessmentName from location.state to component state
    if (location.state?.assessmentName && !assessmentNameState) {
      setAssessmentNameState(location.state.assessmentName);
    }

    // Load filters for the initial tab
    const savedFilters = getAssessmentSubmissionFilterFromStorage(initialTab);
    if (savedFilters) {
      setFilters(savedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync active tab when query param changes (e.g., browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const tabFromQuery = tabParam ? getTabFromQueryParam(tabParam) : 'summary';

    if (tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);

      // Load filters for the new tab
      const savedFilters = getAssessmentSubmissionFilterFromStorage(tabFromQuery);
      if (savedFilters) {
        setFilters(savedFilters);
      } else {
        setFilters({
          parentStatusFilters: {},
          childStatusFilters: {},
          parentStatus: null,
          childStatus: null,
          submittedDate: null
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveAssessmentSubmissionFilterToStorage(activeTab, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab]);

  const handleApplyFilter = (filterData) => {
    const newFilters = {
      parentStatusFilters: filterData.parentStatusFilters || {},
      childStatusFilters: filterData.childStatusFilters || {},
      parentStatus: filterData.parentStatus,
      childStatus: filterData.childStatus,
      submittedDate: filterData.submittedDate
    };
    setFilters(newFilters);
    setShowFilter(false);
  };

  const handleResetFilter = () => {
    const resetFilters = {
      parentStatusFilters: {},
      childStatusFilters: {},
      parentStatus: null,
      childStatus: null,
      submittedDate: null
    };
    setFilters(resetFilters);
    saveAssessmentSubmissionFilterToStorage(activeTab, resetFilters);
  };

  const handleTabChange = (tabKey) => {
    // Load saved filters for the new tab instead of resetting
    const savedFilters = getAssessmentSubmissionFilterFromStorage(tabKey);
    if (savedFilters) {
      setFilters(savedFilters);
    } else {
      // If no saved filters, use defaults
      setFilters({
        parentStatusFilters: {},
        childStatusFilters: {},
        parentStatus: null,
        childStatus: null,
        submittedDate: null
      });
    }
    setActiveTab(tabKey);

    // Update URL query parameter
    const tabQueryParam = getQueryParamFromTab(tabKey);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabQueryParam);
    setSearchParams(newSearchParams, { replace: true });
  };

  const renderActiveFilters = () => {
    const activeFilters = [];
    
    // Status label mappings
    const parentStatusLabels = {
      COMPLETED: t("Completed.label"),
      IN_PROGRESS: t("InProgress.label"),
      NOT_STARTED: t("NotStarted.label")
    };

    const childStatusLabels = {
      DRAFT: t("NotStarted.label"),
      IN_PROGRESS: t("InProgress.label"),
      PENDING_APPROVAL_LEVEL_1: t("PendingApproval1st.label"),
      PENDING_APPROVAL_LEVEL_2: t("PendingApproval2nd.label"),
      PENDING_APPROVAL_LEVEL_3: t("PendingApproval3rd.label"),
      CORRECTION_PENDING: t("CorrectionPending.label"),
      COMPLETED: t("Completed.label"),
      NOT_STARTED: t("NotStarted.label")
    };

    // For Summary tab - show parent status filters
    if (activeTab === 'summary') {
      const selectedParentStatuses = Object.entries(filters.parentStatusFilters || {})
        .filter(([_, isSelected]) => isSelected)
        .map(([status]) => parentStatusLabels[status])
        .filter(Boolean);

      if (selectedParentStatuses.length > 0) {
        activeFilters.push(
          <span key="parent-status-filter" className="Filter_status">
            <h6>{t("Status.label")}: </h6>
            {selectedParentStatuses.join(", ")}
          </span>
        );
      }

      const selectedChildStatuses = Object.entries(filters.childStatusFilters || {})
        .filter(([_, isSelected]) => isSelected)
        .map(([status]) => childStatusLabels[status])
        .filter(Boolean);

      if (selectedChildStatuses.length > 0) {
        activeFilters.push(
          <span key="child-status-filter" className="Filter_status">
            <h6>{t("Status.label")}: </h6>
            {selectedChildStatuses.join(", ")}
          </span>
        );
      }
    } else {
      // For Responses and Approvals tabs - show child status filters
      const selectedChildStatuses = Object.entries(filters.childStatusFilters || {})
        .filter(([_, isSelected]) => isSelected)
        .map(([status]) => childStatusLabels[status])
        .filter(Boolean);

      if (selectedChildStatuses.length > 0) {
        activeFilters.push(
          <span key="status-filter" className="Filter_status">
            <h6>{t("Status.label")}: </h6>
            {selectedChildStatuses.join(", ")}
          </span>
        );
      }
    }

    return activeFilters.length > 0 ? (
      <div className="qualitative-filter-status" style={{ position: 'absolute', right: '0px', bottom: '-40px'}}>
        {activeFilters}
      </div>
    ) : null;
  };

  const handleDownloadExcel = () => {
    if (!id) return;

    setLoaderDisplay(true);
    FormService.downloadFormData(id, 0, 10, 'parentBranch,asc', true)
      .then((response) => {
        setLoaderDisplay(false);
        if (response.data && Array.isArray(response.data)) {
          // Helper function to format status
          const formatStatus = (item) => {
            if (item.tokenStatus === 'REVOKED') {
              return STATUS['NO_PERMISSION'] || 'No Permission';
            }
            if (item.status === 'DRAFT') {
              return STATUS['NOT_STARTED'] || 'Not Started';
            }
            if (item.status === 'PENDING_APPROVAL') {
              const approvalLevel = item.pendingApprovalLevel;
              return approvalLevel
                ? `Pending Approval at Level ${approvalLevel}`
                : STATUS['PENDING_APPROVAL'] || 'Pending Approval';
            }
            return STATUS[item.status] || item.status || '';
          };

          // Helper function to format data owners emails
          const formatDataOwners = (emails) => {
            if (!emails) return '';
            const emailString = Array.isArray(emails) ? emails.join(',') : emails.toString();
            if (!emailString.trim()) return '';
            return emailString.split(',').map(email => email.trim()).filter(email => email).join(', ');
          };

          // Extract and format relevant data, excluding assessment, branch, and form objects
          const formattedData = response.data.map(item => {
            const formatted = {};

            // Extract useful information from nested objects before excluding them
            if (self === 'true') {
              // Parent Entity
              formatted['Parent Entity'] = item.branch?.parent
                ? item.branch.parent.name
                : item.branch?.name || '';

              // Child Entity
              formatted['Child Entity'] = item.branch?.parent
                ? item.branch.name
                : '';

              // Data Owners
              formatted['Data Owners'] = formatDataOwners(item.branch?.emails);
            } else {
              // Supplier
              formatted['Supplier'] = item.submittedByCompany?.name || '';
            }

            // Shared Date (from assessment object, but format nicely)
            formatted['Shared Date'] = item.assessment?.sharedOn
              ? moment(item.assessment.sharedOn).format("MMM DD, YYYY")
              : '';

            // Submitted Date
            formatted['Submitted Date'] = item.submissionDate
              ? moment(item.submissionDate).format("MMM DD, YYYY, h:mm a")
              : '';

            // Status
            formatted['Status'] = formatStatus(item);

            // Last Modified Date
            formatted['Last Modified Date'] = item.lastModifiedDate
              ? moment(item.lastModifiedDate).format("MMM DD, YYYY, h:mm a")
              : '';

            return formatted;
          });

          // Define column order for better readability
          const columnOrder = self === 'true'
            ? ['Parent Entity', 'Child Entity', 'Data Owners', 'Shared Date', 'Submitted Date', 'Status', 'Last Modified Date']
            : ['Supplier', 'Shared Date', 'Submitted Date', 'Status', 'Last Modified Date'];

          // Create workbook and worksheet
          const workbook = XLSX.utils.book_new();
          const worksheet = XLSX.utils.aoa_to_sheet([]);

          // Set column widths for better readability
          const colWidths = columnOrder.map(() => ({ wch: 20 }));
          worksheet['!cols'] = colWidths;

          // Add header row with bold formatting
          columnOrder.forEach((header, colIndex) => {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
            worksheet[cellAddress] = {
              t: 's', // string type
              v: header,
              s: {
                font: { bold: true }
              }
            };
          });

          // Update worksheet range to include header row
          worksheet['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: formattedData.length, c: columnOrder.length - 1 }
          });

          // Add data rows
          formattedData.forEach((item, rowIndex) => {
            columnOrder.forEach((header, colIndex) => {
              const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
              const value = item[header];
              worksheet[cellAddress] = {
                t: typeof value === 'number' ? 'n' : 's',
                v: value !== null && value !== undefined ? value : ''
              };
            });
          });

          XLSX.utils.book_append_sheet(workbook, worksheet, "Form Responses");

          // Generate filename
          const fileName = `Data Collection Responses-${moment().format('DD-MM-YYYY HH-mm')}.xlsx`;

          // Download the file with cell styles enabled
          XLSX.writeFile(workbook, fileName, { cellStyles: true });
        } else {
          console.error('Invalid response data format');
        }
      })
      .catch((error) => {
        setLoaderDisplay(false);
        console.error('Error downloading form data:', error);
      });
  };

  return (
    <>
      <div className="FormsList">
        <div className="List_header">
          <Breadcrumb className="h5">
            <Breadcrumb.Item onClick={() => {
              navigate(`/company_admin/assessments`,
                {
                  state: {
                    isQualitative: qualitativeDataStatus ? true : false
                  },
                }
              );
            }} > {t("DataCollectionEvents.label")}</Breadcrumb.Item>
            <Breadcrumb.Item active> {getAssessmentTitle()}</Breadcrumb.Item>
          </Breadcrumb>
          <div className="List_header_search">
            {renderActiveFilters()}
            <Button
              variant="headbtn"
              onClick={() => setShowFilter(!showFilter)}
              className="btn  filter-icon"
            >
              <BiFilter />
            </Button>
            {activeTab === 'responses' && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="download-excel-tooltip">{t("AssessmentSubmissionList.downloadExcelTooltip.label")}</Tooltip>}
              >
                <Button
                  variant="headbtn"
                  onClick={handleDownloadExcel}
                  className="btn">
                  <BsDownload />
                </Button>
              </OverlayTrigger>
            )}
            <div className="List_table-card_Search_01">
              <div className="List_table-card_Search_Box">
                <Form onSubmit={(e) => { e.preventDefault(); }}>
                  <input
                    type="text"
                    className="form-control"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("SearchByEntity.label")}
                  />
                  <a
                    className="Search_iocnBox cursor"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSearch(inputValue);
                    }}
                  >
                    <BiSearch />
                  </a>
                </Form>
              </div>
            </div>

          </div>
        </div>

        <div className="List_Table_01 mt-2">
          <div className="row m-0">
            <div className={showFilter ? "col-lg-12 col-md-12" : "col-12"}>
              <Tabs
                activeKey={activeTab}
                id="assessment-tabs"
                className="Companysettings-tab"
                onSelect={handleTabChange}
              >
                <Tab eventKey="summary" title={t("Summary.label")}>
                  <SummaryTab
                    t={t}
                    id={id}
                    assessmentName={assessmentNameState || assessmentName}
                    searchKey={searchKey}
                    filters={filters}
                    activeTab={activeTab}
                  />
                </Tab>
                <Tab eventKey="responses" title={t("Responses.label")}>
                  <ResponsesTab
                    t={t}
                    id={id}
                    self={self}
                    qualitativeDataStatus={qualitativeDataStatus}
                    currentRoute={currentRoute}
                    searchKey={searchKey}
                    onSearchChange={handleSearch}
                    filters={filters}
                    activeTab={activeTab}
                  />
                </Tab>
                <Tab eventKey="approvals" title={t("Approvals.label")}>
                  <ApprovalsTab
                    t={t}
                    id={id}
                    self={self}
                    currentRoute={currentRoute}
                    searchKey={searchKey}
                    filters={filters}
                    activeTab={activeTab}
                  />
                </Tab>
              </Tabs>
            </div>
            {showFilter && (
              <AssessmentFilterPanel
                onApplyFilter={handleApplyFilter}
                onResetFilter={handleResetFilter}
                onFilterHide={setShowFilter}
                initialFilters={filters}
                activeTab={activeTab}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default withTranslation()(AssessmentSubmissionList);
